import { useWizard } from '../../../context/WizardContext';
import { NavigationButtons } from '../../../components/wizard/NavigationButtons';
import { cn } from '../../../lib/utils';
import { useTranslation } from '../../../context/LanguageContext';
import type { TranslationKey } from '../../../i18n';
import { Check } from 'lucide-react';

// The selected styles are translucent tints rather than solid `-50` fills: the
// wizard sits on a dark surface, where a light fill reads as a blown-out card.
const LEVELS: {
  value: number;
  label: string;
  fullNameKey: TranslationKey;
  classesKey: TranslationKey;
  emoji: string;
  color: string;
  selected: string;
  selectedText: string;
}[] = [
  {
    value: 10,
    // 'SSC' / 'HSC' are the board's own initialisms and are used verbatim in both
    // languages, so they are not translated.
    label: 'SSC',
    fullNameKey: 'wizard.classSscFull',
    classesKey: 'wizard.classSscRange',
    emoji: '📘',
    color: 'from-sky-400 to-blue-500',
    selected: 'border-sky-500/50 bg-sky-500/10',
    selectedText: 'text-sky-300',
  },
  {
    value: 12,
    label: 'HSC',
    fullNameKey: 'wizard.classHscFull',
    classesKey: 'wizard.classHscRange',
    emoji: '🎓',
    color: 'from-brand-500 to-violet-600',
    selected: 'border-violet-500/50 bg-violet-500/10',
    selectedText: 'text-violet-300',
  },
];

export function Step0Class() {
  const { state, setClass } = useWizard();
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-white">{t('wizard.classTitle')}</h2>
        <p className="text-sm text-slate-500 mt-1">{t('wizard.classSubtitle')}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {LEVELS.map((level) => {
          const isSelected = state.selectedClass === level.value;
          return (
            <button
              key={level.value}
              type="button"
              onClick={() => setClass(level.value)}
              className={cn(
                'relative flex flex-col items-center gap-4 rounded-2xl border-2 p-8 transition-all duration-200 focus:outline-none group',
                isSelected
                  ? `${level.selected} shadow-glow`
                  : 'border-slate-700 bg-slate-700/50 hover:border-slate-600 hover:shadow-card-hover hover:-translate-y-0.5'
              )}
            >
              {isSelected && (
                <span className="absolute top-3 right-3 flex h-5 w-5 items-center justify-center rounded-full bg-brand-600 text-white">
                  <Check className="h-3 w-3" strokeWidth={3} />
                </span>
              )}

              <div
                className={cn(
                  'flex h-16 w-16 items-center justify-center rounded-2xl text-3xl shadow-md transition-transform duration-300 group-hover:scale-105',
                  `bg-gradient-to-br ${level.color}`
                )}
              >
                {level.emoji}
              </div>

              <div className="text-center">
                <p
                  className={cn(
                    'text-3xl font-extrabold tracking-tight',
                    isSelected ? level.selectedText : 'text-white'
                  )}
                >
                  {level.label}
                </p>
                <p className="text-sm font-medium text-slate-400 mt-1">{t(level.fullNameKey)}</p>
                <span className="mt-2 inline-block rounded-full bg-slate-700 px-2.5 py-0.5 text-xs font-medium text-slate-400">
                  {t(level.classesKey)}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      <NavigationButtons />
    </div>
  );
}
